//usestore
import { create } from 'zustand';
import { generateRequestId } from '../observability/requestId.js';
import { logInfo, logWarn } from '../observability/logger.js';

const loadingMessageKey = 'loading-update';

const useStore = create((set, get) => ({
  olts: [],
  onus: [],
  splitters: [],
  pendingRequests: {},
  isUserInfoLoaded: false, // Estado de carregamento do user info
  pendingUpdates: 0, // Estado para rastrear atualizações pendentes

  setOlts: (olts) => set({ olts }),
  setOnus: (onus) => set({ onus }),
  setSplitters: (splitters) => set({ splitters }),
  setIsUserInfoLoaded: (isLoaded) => {
    set((state) => ({
      isUserInfoLoaded: isLoaded,
    }));
  },

  startPendingRequest: (requestId, messageApi) =>
    set((state) => {
      if (!requestId || state.pendingRequests[requestId]) {
        return state;
      }

      const currentCount = Object.keys(state.pendingRequests).length;
      if (currentCount === 0) {
        messageApi.open({
          key: loadingMessageKey,
          type: 'loading',
          content: 'Atualizando dados...',
          duration: 0,
        });
      }

      return {
        pendingRequests: {
          ...state.pendingRequests,
          [requestId]: true,
        },
        pendingUpdates: currentCount + 1,
        isUserInfoLoaded: false,
      };
    }),

  finishPendingRequest: (requestId, messageApi) =>
    set((state) => {
      if (!requestId || !state.pendingRequests[requestId]) {
        return state;
      }

      const nextPendingRequests = { ...state.pendingRequests };
      delete nextPendingRequests[requestId];
      const remaining = Object.keys(nextPendingRequests).length;

      if (remaining === 0) {
        messageApi.open({
          key: loadingMessageKey,
          type: 'success',
          content: 'Atualização concluída com sucesso!',
          duration: 2,
        });
      }

      return {
        pendingRequests: nextPendingRequests,
        pendingUpdates: remaining,
        isUserInfoLoaded: remaining === 0,
      };
    }),

  callWSget_user_info: (socket, messageApi, sourceOnus = null) => {
    const onus = Array.isArray(sourceOnus) ? sourceOnus : get().onus;

    if (!onus.length) {
      logWarn('ws.user_info.skipped', { reason: 'no_onus' });
      return;
    }

    const users = [...new Set(
      onus
        .map(onu => onu.username)
        .filter(username => username && !username.startsWith("Erro:"))
    )];

    if (!users.length) {
      logWarn('ws.user_info.skipped', { reason: 'no_valid_usernames' });
      return;
    }

    const requestId = generateRequestId('ws-user-info');

    const message = {
      action: "get_user_info",
      request_id: requestId,
      data: {
        usernames: users,
      }
    };

    get().startPendingRequest(requestId, messageApi);

    socket.send(JSON.stringify(message));
    logInfo('ws.request.sent', {
      request_id: requestId,
      action: 'get_user_info',
      usernames_count: users.length,
    });
  },

  handleWebSocketMessage: (parsedMessage, socket, messageApi) => {
    parsedMessage = JSON.parse(parsedMessage);
    logInfo('ws.response.received', {
      request_id: parsedMessage.request_id,
      action: parsedMessage.action,
      status: parsedMessage.status,
    });

    const { setOlts, setOnus, setSplitters, finishPendingRequest } = get();
    finishPendingRequest(parsedMessage.request_id, messageApi);

    // Processa a resposta para get_olts_info
    if (parsedMessage.action === 'get_olts_info' && parsedMessage.status === 'success') {
        setOlts(parsedMessage.data);
    }

    // Processa a resposta para get_onu_desc
    if (parsedMessage.action === 'get_onu_desc' && parsedMessage.status === 'success') {
        const currentOnus = get().onus;
        const onusData = parsedMessage.data.flat().map(({ olt_id, olt_name, slot, port, onus }) =>
            onus.map(onu => ({
                ...onu,
                olt_id,
                olt_name,
                slot,
                port,
            }))
        ).flat();

        const updatedOnus = [...currentOnus];

        onusData.forEach((newOnu) => {
            const index = updatedOnus.findIndex(onu =>
                onu.id === newOnu.id &&
                onu.olt_id === newOnu.olt_id &&
                onu.slot === newOnu.slot &&
                onu.port === newOnu.port
            );

            if (index !== -1) {
                updatedOnus[index] = { ...updatedOnus[index], ...newOnu };
            } else {
                updatedOnus.push(newOnu);
            }
        });

        set({ onus: updatedOnus });

        // Chama a função para enviar get_user_info apenas para o lote recem-atualizado.
        get().callWSget_user_info(socket, messageApi, onusData);
    }

    // Processa a resposta para get_user_info
    if (parsedMessage.action === 'get_user_info' && parsedMessage.status === 'success') {
        const currentOnus = get().onus;

        const userInfoData = parsedMessage.data.reduce((acc, userInfo) => {
            const username = userInfo.username;
            acc[username] = userInfo;
            return acc;
        }, {});

        const updatedOnus = [...currentOnus];

        // Regra de dominio: enriquecimento de user_info e associado por username.
        updatedOnus.forEach((onu, index) => {
            if (userInfoData[onu.username]) {
                updatedOnus[index] = { ...updatedOnus[index], ...userInfoData[onu.username] };
            }
        });

        set({ onus: updatedOnus });
    }

    // Processa a resposta para get_onu_status
    if (parsedMessage.action === 'get_onu_status' && parsedMessage.status === 'success') {
        const currentOnus = get().onus;
        const statusData = parsedMessage.data.flat().map(({ olt_id, slot, port, onus }) =>
            onus.map(({ id, status }) => ({
                id,
                olt_id,
                slot,
                port,
                status,
            }))
        ).flat();

        const updatedOnus = [...currentOnus];

        statusData.forEach((newStatus) => {
            const index = updatedOnus.findIndex(onu =>
                onu.id === newStatus.id &&
                onu.olt_id === newStatus.olt_id &&
                onu.slot === newStatus.slot &&
                onu.port === newStatus.port
            );

            if (index !== -1) {
                updatedOnus[index] = { ...updatedOnus[index], status: newStatus.status };
            }
        });

        set({ onus: updatedOnus });
    }

    // Processa a resposta para get_onu_serial
    if (parsedMessage.action === 'get_onu_serial' && parsedMessage.status === 'success') {
        const currentOnus = get().onus;
        const serialData = parsedMessage.data.flat().map(({ olt_id, slot, port, onus }) =>
            onus.map(({ id, serial }) => ({
                id,
                olt_id,
                slot,
                port,
                serial,
            }))
        ).flat();

        const updatedOnus = [...currentOnus];

        serialData.forEach((newSerial) => {
            const index = updatedOnus.findIndex(onu =>
                onu.id === newSerial.id &&
                onu.olt_id === newSerial.olt_id &&
                onu.slot === newSerial.slot &&
                onu.port === newSerial.port
            );

            if (index !== -1) {
                updatedOnus[index] = { ...updatedOnus[index], serial: newSerial.serial };
            }
        });

        set({ onus: updatedOnus });
    }

    // Processa a resposta para get_onu_down_cause
    if (parsedMessage.action === 'get_onu_down_cause' && parsedMessage.status === 'success') {
        const currentOnus = get().onus;
        const downCauseData = parsedMessage.data.flat().map(({ olt_id, slot, port, onus }) =>
            onus.map(({ id, down_cause }) => ({
                id,
                olt_id,
                slot,
                port,
                down_cause,
            }))
        ).flat();

        const updatedOnus = [...currentOnus];

        downCauseData.forEach((newCause) => {
            const index = updatedOnus.findIndex(onu =>
                onu.id === newCause.id &&
                onu.olt_id === newCause.olt_id &&
                onu.slot === newCause.slot &&
                onu.port === newCause.port
            );

            if (index !== -1) {
                updatedOnus[index] = { ...updatedOnus[index], down_cause: newCause.down_cause };
            } else {
                updatedOnus.push(newCause);
            }
        });

        set({ onus: updatedOnus });
    }

    // Processa a resposta para get_all_splitters
    if (parsedMessage.action === 'get_all_splitters' && parsedMessage.status === 'success') {
        const splittersData = parsedMessage.data.map(splitter => ({
            splitter_id: splitter.splitter_id,
            integrated_splitter_id: parseInt(splitter.integrated_splitter_id) || null,
            splitter_name: splitter.splitter_name,
            value: splitter.integration_code,
            olt_title: splitter.olt_title,
            slot_olt: splitter.slot_olt,
            port_olt: splitter.port_olt,
            smartolt_olt_id: splitter.smartolt_olt_id,
            lat: splitter.lat,
            lng: splitter.lng,
            out_ports: splitter.out_ports,
        }));
        setSplitters(splittersData);
    }
}
  }));

export default useStore;
